import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import LogoutButton from "layouts/logout";

import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { ServerBaseUrlForStaticImages } from "constants";

const columns = [
    { Header: "S.No.", accessor: "sno" },
    { Header: "Owner Name", accessor: "owner_name" },
    { Header: "Service Type", accessor: "services_type" },
    { Header: "Mobile", accessor: "mobile_number" },
    { Header: "Options", accessor: "options" },
];

function VerifyServiceProviders() {
    const navigate = useNavigate();

    const [services, setServices] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openImageModal, setOpenImageModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [checkedImages, setCheckedImages] = useState({});
    const [allChecked, setAllChecked] = useState(false);
    const [fullScreenImage, setFullScreenImage] = useState(null);

    // Service images
    const imageKeys = ["profile_photo", "work_photo", "work_place_gps"];

    useEffect(() => {
        fetchUnverifiedServices();
    }, []);

    const fetchUnverifiedServices = async () => {
        try {
            const res = await api.get("/services_register/fetch_unverified_services");
            setServices(res.data.services || []);
            setPage(0);
        } catch {
            Swal.fire("Error", "Failed to load service providers", "error");
        }
    };

    /* ---------------- IMAGE CHECK LOGIC ---------------- */

    const proofDocs = selectedService?.idproof_photo
        ? selectedService.idproof_photo.split(",")
        : [];

    const allKeys = [
        ...imageKeys.filter((k) => selectedService?.[k]),
        ...proofDocs.map((_, i) => `proof-${i}`),
    ];

    useEffect(() => {
        const init = {};
        allKeys.forEach((k) => (init[k] = false));
        setCheckedImages(init);
    }, [selectedService]);

    useEffect(() => {
        setAllChecked(allKeys.length > 0 && allKeys.every((k) => checkedImages[k]));
    }, [checkedImages, allKeys]);

    /* ---------------- VERIFY ---------------- */

    const handleVerify = async () => {
        const confirm = await Swal.fire({
            title: "Verify Service Provider?",
            text: "All documents are verified. Continue?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Verify",
        });

        if (!confirm.isConfirmed) return;

        try {
            await api.put(`/services_register/verify_service/${selectedService.id}`);
            Swal.fire("Success", "Service provider verified", "success");
            setOpenImageModal(false);
            setSelectedService(null);
            fetchUnverifiedServices();
        } catch {
            Swal.fire("Error", "Verification failed", "error");
        }
    };

    /* ---------------- REJECT ---------------- */

    const handleReject = async () => {
        const result = await Swal.fire({
            title: "Reject Service Provider?",
            input: "text",
            inputLabel: "Reason",
            inputValidator: (v) => (!v ? "Reason is required" : null),
            showCancelButton: true,
            icon: "warning",
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(
                `/services_register/reject_service_reason/${selectedService.id}`,
                { reason: result.value }
            );
            await api.post(`/services_register/reject_services/${selectedService.id}`);

            Swal.fire("Rejected", "Service provider rejected", "success");
            setOpenImageModal(false);
            setSelectedService(null);
            fetchUnverifiedServices();
        } catch {
            Swal.fire("Error", "Rejection failed", "error");
        }
    };

    /* ---------------- SEARCH + PAGINATION ---------------- */

    const filtered = services.filter((s) => {
        const q = searchQuery.toLowerCase();
        return (
            s.owner_name.toLowerCase().includes(q) ||
            s.services_type.toLowerCase().includes(q) ||
            s.mobile_number.includes(q)
        );
    });

    const start = page * rowsPerPage;
    const paginated = filtered.slice(start, start + rowsPerPage);

    const rows = paginated.map((s, i) => ({
        sno: start + i + 1,
        owner_name: s.owner_name,
        services_type: s.services_type,
        mobile_number: s.mobile_number,
        options: (
            <IconButton
                color="success"
                onClick={() => {
                    setSelectedService(s);
                    setOpenImageModal(true);
                }}
            >
                <ImageIcon />
            </IconButton>
        ),
    }));

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end">
                <LogoutButton onLogout={() => navigate("/login")} />
            </MDBox>

            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg">
                                <MDTypography color="white">Verify Service Providers</MDTypography>
                            </MDBox>

                            <MDBox p={2}>
                                <TextField
                                    fullWidth
                                    label="Search"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setPage(0);
                                    }}
                                />
                            </MDBox>

                            <MDBox p={2}>
                                <DataTable
                                    table={{ columns, rows }}
                                    entriesPerPage={{ defaultValue: rowsPerPage }}
                                    pagination={{
                                        page,
                                        rowsPerPage,
                                        count: filtered.length,
                                        onPageChange: (_, p) => setPage(p),
                                        onRowsPerPageChange: (_, s) => {
                                            setRowsPerPage(s);
                                            setPage(0);
                                        },
                                    }}
                                    noEndBorder
                                />
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* IMAGE VERIFY MODAL */}
            <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md" fullWidth>
                <DialogTitle>Verify Documents</DialogTitle>

                <DialogContent>
                    <Grid container spacing={2}>
                        {imageKeys.map(
                            (key) =>
                                selectedService?.[key] && (
                                    <Grid item xs={4} key={key}>
                                        <img
                                            src={`${ServerBaseUrlForStaticImages}services_register/media/${selectedService[key]}`}
                                            style={{ width: "100%", cursor: "pointer" }}
                                            onClick={() =>
                                                setFullScreenImage(
                                                    `${ServerBaseUrlForStaticImages}services_register/media/${selectedService[key]}`
                                                )
                                            }
                                        />
                                        <Checkbox
                                            checked={checkedImages[key] || false}
                                            onChange={(e) =>
                                                setCheckedImages((p) => ({ ...p, [key]: e.target.checked }))
                                            }
                                        />
                                    </Grid>
                                )
                        )}

                        {proofDocs.map((img, i) => {
                            const k = `proof-${i}`;
                            return (
                                <Grid item xs={4} key={k}>
                                    <img
                                        src={`${ServerBaseUrlForStaticImages}services_register/media/${img}`}
                                        style={{ width: "100%" }}
                                    />
                                    <Checkbox
                                        checked={checkedImages[k] || false}
                                        onChange={(e) =>
                                            setCheckedImages((p) => ({ ...p, [k]: e.target.checked }))
                                        }
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <MDButton onClick={() => setOpenImageModal(false)}>Close</MDButton>

                    <Box display="flex" gap={2}>
                        <MDButton color="error" startIcon={<CancelIcon />} onClick={handleReject}>
                            Reject
                        </MDButton>

                        <MDButton
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={handleVerify}
                            disabled={!allChecked}
                        >
                            Verify
                        </MDButton>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* FULL IMAGE */}
            <Dialog open={Boolean(fullScreenImage)} onClose={() => setFullScreenImage(null)} maxWidth="xl">
                <DialogContent>
                    <img src={fullScreenImage} style={{ width: "100%" }} />
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

export default VerifyServiceProviders;
